import {
  AutoComplete,
  Button,
  Flex,
  Form,
  Input,
  InputRef,
  List,
  Select,
  Typography,
} from "antd";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useRef, useState } from "react";
import timezones from "timezones-list";
import { openNotification } from "../../../Components/Notification";
import { Organization } from "../../../Entities/Organization";
import useAccess from "../../../Hooks/useAccess";
import currencyService from "../../../Service/currency.service";
import organizationService from "../../../Service/organization.service";
import LogoDetails from "./LogoDetails";
import { industriesOptions } from "../../../Utils/industry-constants";
import SubscriptionWarning from "../../../Components/SubscriptionWarning";

const timezoneOptions = timezones.map((timezone) => ({
  value: timezone.tzCode,
  label: (
    <List size="small">
      <List.Item style={{ padding: 0 }}>
        <List.Item.Meta
          title={
            <Typography.Text style={{ fontWeight: "normal" }}>
              {timezone.label}
            </Typography.Text>
          }
          description={
            <small>
              {timezone.name} {timezone.utc}
            </small>
          }
        />
      </List.Item>
    </List>
  ),
}));
interface OrgDetailsProps {
  organizationDetails: Organization;
  setOrganizationDetails: (details: Organization) => void;
}

const OrgDetails: React.FC<OrgDetailsProps> = ({
  organizationDetails,
  setOrganizationDetails,
}) => {
  const { currentLimits, currentPlan } = useAccess();
  const domainInputRef = useRef<InputRef>(null);
  const [currencies, setCurrencies] = useState<
    { currency_code: string; country: string }[]
  >([]);
  const [selectedCurrency, setSelectedCurrency] = useState<string>("");

  const handleCurrencyChange = (value: string) => {
    setSelectedCurrency(value);
  };

  const [form] = Form.useForm();
  const [domainError, setDomainError] = useState<string | null>(null);
  const [isDomainAvailable, setIsDomainAvailable] = useState<boolean | null>(
    null,
  );
  const [editedDomain, setEditedDomain] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    fetchCurrencies();
  }, []);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const fieldMap: Record<string, string> = {
        "#orgName": "orgName",
        "#orgWebsite": "orgWebsite",
        "#gst": "gst",
        "#domain": "orgDomain",
        "#orgDomain": "orgDomain",
        "#timeZone": "timeZone",
        "#currency": "currency",
        "#supportEmail": "supportEmail",
        "#industry": "industry",
        "#description": "description",
        "#logo": "logo",
      };

      const fieldName = fieldMap[hash];
      if (fieldName) {
        // Small delay to ensure tab is active and DOM is ready
        const timeoutId = setTimeout(() => {
          const checkForElement = setInterval(() => {
            let element: HTMLElement | null = null;

            if (fieldName === "logo") {
              // For logo, find the logo upload section
              element = document.querySelector(
                ".logo-details-section",
              ) as HTMLElement;
            } else {
              // For form fields, find the input
              element = document.querySelector(
                `input[name="${fieldName}"], textarea[name="${fieldName}"]`,
              ) as HTMLInputElement;

              // If not found, try to find the parent Form.Item
              if (!element) {
                const formItem = document.querySelector(
                  `[data-field="${fieldName}"]`,
                ) as HTMLElement;
                if (formItem) {
                  element = formItem.querySelector(
                    "input, textarea, .ant-select-selector",
                  ) as HTMLElement;
                }
              }
            }

            if (element) {
              clearInterval(checkForElement);

              const rect = element.getBoundingClientRect();
              const scrollTop =
                window.pageYOffset || document.documentElement.scrollTop;
              const targetPosition =
                rect.top + scrollTop - window.innerHeight / 2;

              // Scroll to the element
              window.scrollTo({
                top: targetPosition,
                behavior: "smooth",
              });

              // Then focus and highlight
              requestAnimationFrame(() => {
                if (!element) return;

                if (
                  fieldName !== "logo" &&
                  fieldName !== "timeZone" &&
                  fieldName !== "currency"
                ) {
                  form.getFieldInstance(fieldName)?.focus();
                }
                element.classList.add("highlight-field");

                // Remove highlight after animation and clear hash
                setTimeout(() => {
                  if (element) {
                    element.classList.remove("highlight-field");
                  }
                  // Clear hash after highlighting
                  if (window.location.hash === hash) {
                    window.history.replaceState(
                      null,
                      "",
                      window.location.pathname + window.location.search,
                    );
                  }
                }, 2000);
              });
            }
          }, 100);

          // Cleanup interval after 5 seconds if element is not found
          setTimeout(() => {
            clearInterval(checkForElement);
          }, 5000);
        }, 300);

        return () => {
          clearTimeout(timeoutId);
        };
      }
    }
  }, [organizationDetails, form]);

  useEffect(() => {
    // fetchCurrencies();
    if (organizationDetails) {
      const domain = organizationDetails?.orgDomain?.split(".")[0];
      form.setFieldsValue({
        orgName: organizationDetails?.orgName || "",
        orgDomain: domain || "",
        orgWebsite: organizationDetails?.orgWebsite || "",
        gst: organizationDetails?.gstNumber || "",
        timeZone: organizationDetails?.timeZone || "",
        currency: organizationDetails?.currency || "",
        supportEmail: organizationDetails?.supportEmail || "",
        industry: organizationDetails?.industry || "",
        description: organizationDetails?.description || "",
      });
      setEditedDomain(domain || "");
    }
  }, [organizationDetails, form]);

  const fetchCurrencies = async () => {
    try {
      const currencyData = await currencyService.getAllCurrencies();

      const formattedCurrencies = currencyData.map((item: any) => ({
        currency_code: item.currency_code,
        country: item.country,
      }));

      setCurrencies(formattedCurrencies);
    } catch (error) {
      console.error("Error fetching currencies:", error);
    }
  };

  const debouncedCheckDomainAvailability = useCallback(
    debounce(async (domain: string) => {
      try {
        if (
          domain ===
          (organizationDetails?.orgDomain?.replace(".jobzy.ai", "") || "")
        ) {
          setIsDomainAvailable(true);
          setDomainError(null);
          return;
        }
        const availability = await organizationService.checkDomainAvailability(
          domain,
        );
        setIsDomainAvailable(availability);
        setDomainError(availability ? null : "Domain is already taken");
      } catch (error) {
        console.error("Error checking domain availability:", error);
      }
    }, 1000),
    [organizationDetails?.orgDomain],
  );

  const handleFormValuesChange = (changedValues: any) => {
    if (changedValues.orgDomain) {
      if (!currentLimits?.customDomainLinking) {
        setOpen(true);
        return;
      }
      setEditedDomain(changedValues.orgDomain);
      debouncedCheckDomainAvailability(changedValues.orgDomain);
    }
  };

  const validateDomain = (_: any, value: string) => {
    const domainRegex = /^[a-z0-9-]+$/;
    if (!value || domainRegex.test(value)) {
      return Promise.resolve();
    }
    return Promise.reject(
      new Error(
        "Domain name can only contain lowercase letters, numbers, and hyphens. No other symbols are allowed.",
      ),
    );
  };

  const handleCancel = () => {
    const domain = organizationDetails?.orgDomain?.split(".")[0];
    form.setFieldsValue({
      orgName: organizationDetails?.orgName || "",
      orgDomain: domain || "",
      orgWebsite: organizationDetails?.orgWebsite || "",
      gst: organizationDetails?.gstNumber || "",
      timeZone: organizationDetails?.timeZone || "",
      currency: organizationDetails?.currency || "",
      supportEmail: organizationDetails?.supportEmail || "",
      industry: organizationDetails?.industry || "",
      description: organizationDetails?.description || "",
    });
    setEditedDomain(domain || "");
  };

  const handleUpdate = async () => {
    try {
      const values = await form.validateFields();

      if (!organizationDetails) {
        return;
      }

      organizationDetails.orgName = values.orgName?.trim() ?? "";
      organizationDetails.orgWebsite = values.orgWebsite?.trim() ?? "";
      organizationDetails.gstNumber = values.gst?.trim() ?? "";
      organizationDetails.timeZone = values.timeZone?.trim() ?? "";
      organizationDetails.currency = values.currency?.trim() ?? "";
      organizationDetails.supportEmail = values.supportEmail?.trim() ?? "";
      organizationDetails.industry = values.industry?.trim() ?? "";
      organizationDetails.description = values.description?.trim() ?? "";

      if (domainError === null && isDomainAvailable === true) {
        organizationDetails.orgDomain = `${values.orgDomain.trim()}${
          domainSuffix ? `.${domainSuffix}` : ""
        }`;
      }

      const response = await organizationService.updateOrganization(
        organizationDetails,
      );

      if (response.status === 400) {
        openNotification("error", response?.message, "");
        return;
      }

      setOrganizationDetails(response);
      openNotification("success", "", "Organization details updated.");
    } catch (error) {
      console.error(
        "Form validation error or error saving organization details:",
        error,
      );
      openNotification("error", "", "Failed to update organization details.");
    }
  };

  const domainSuffix = organizationDetails?.orgDomain
    .split(".")
    .slice(1)
    .join(".");

  const groupCurrenciesByCode = (currencyData: any) => {
    return currencyData.reduce((acc: any, currency: any) => {
      const { currency_code } = currency;
      if (!acc[currency_code]) {
        acc[currency_code] = [];
      }
      acc[currency_code].push(currency);
      return acc;
    }, {});
  };

  const handleCustomDomainClick = () => {
    if (!currentLimits?.customDomainLinking) {
      const domain = organizationDetails?.orgDomain?.split(".")[0];
      form.setFieldsValue({
        orgDomain: domain || "",
      });
      setEditedDomain(domain || "");
      setOpen(true);
      return;
    }
  };

  const groupedCurrencies = groupCurrenciesByCode(currencies);

  return (
    <section className="organization-details-section">
      <div className="grid-container">
        <LogoDetails
          organizationDetails={organizationDetails}
          setOrganizationDetails={setOrganizationDetails}
          limits={currentLimits}
        />
        <div className="grid-item-large">
          <div className="org-details-form-wrapper hide-scroll-bar">
            <Form
              layout="vertical"
              form={form}
              className="full-width-form"
              onValuesChange={handleFormValuesChange}
              scrollToFirstError
              style={{ width: "100%" }}
              autoComplete="off"
            >
              <Form.Item
                label="Organization Name"
                name="orgName"
                data-field="orgName"
                rules={[
                  {
                    required: true,
                    message: "Please enter organization name",
                  },
                ]}
              >
                <Input
                  className="text-input"
                  placeholder="Enter organization name"
                />
              </Form.Item>
              <Form.Item
                label="Organization Website"
                name="orgWebsite"
                data-field="orgWebsite"
                rules={[
                  {
                    required: true,
                    message: "Please enter organization website",
                  },
                  { type: "url" },
                ]}
              >
                <Input
                  className="text-input"
                  placeholder="Enter organization website"
                />
              </Form.Item>
              <Form.Item
                label="GST"
                name="gst"
                data-field="gst"
                style={{ width: "100%" }}
                rules={[
                  {
                    pattern:
                      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{1}[Z]{1}[A-Z0-9]{1}$/,
                    message:
                      "Invalid GST format. Please enter a valid GST number.",
                    validateTrigger: "onBlur",
                  },
                ]}
              >
                <Input
                  style={{ width: "100%" }}
                  className="text-input"
                  placeholder="Enter GST Number"
                />
              </Form.Item>

              <Form.Item
                label="Organization Domain"
                name="orgDomain"
                data-field="orgDomain"
                rules={[
                  {
                    required: true,
                    message: "Please enter organization domain",
                  },
                  { validator: validateDomain },
                ]}
                style={{ width: "100%" }}
              >
                <div className="domain-input-wrapper">
                  <Input
                    ref={domainInputRef}
                    className="text-input domain-input"
                    placeholder="Enter organization domain"
                    value={editedDomain}
                    onChange={(e) => {
                      form.setFieldsValue({ orgDomain: e.target.value });
                      setEditedDomain(e.target.value);
                      debouncedCheckDomainAvailability(e.target.value);
                    }}
                    onClick={handleCustomDomainClick}
                    onFocus={handleCustomDomainClick}
                    addonBefore={"https://"}
                    addonAfter={
                      <span className="domain-suffix">
                        {domainSuffix ? `.${domainSuffix}` : null}
                      </span>
                    }
                  />
                  {/* <span className="domain-suffix">
                    {domainSuffix ? `.${domainSuffix}` : null}
                    {isDomainAvailable === true &&
                      domainError === null &&
                      editedDomain !==
                        organizationDetails?.orgDomain?.split(".")[0] && (
                        <Tooltip title="Domain is available">
                          <CheckCircleOutlined style={{ marginLeft: 8 }} />
                        </Tooltip>
                      )}
                  </span> */}
                </div>
                {domainError && (
                  <Typography.Text type="danger">{domainError}</Typography.Text>
                )}
              </Form.Item>

              <Flex align="center" justify="space-between" gap={16}>
                <Form.Item
                  label="Time Zone"
                  name="timeZone"
                  data-field="timeZone"
                  rules={[
                    { required: true, message: "Please select time zone!" },
                  ]}
                  style={{ width: "100%" }}
                >
                  <AutoComplete
                    className="auto_complete_component"
                    options={timezoneOptions}
                    placeholder="Select Timezone"
                    onSelect={(value) => console.log("timezone", value)}
                    filterOption={(inputValue, option) =>
                      option!.value
                        .toUpperCase()
                        .indexOf(inputValue.toUpperCase()) !== -1
                    }
                    allowClear={true}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
                <Form.Item
                  label="Currency"
                  name="currency"
                  data-field="currency"
                  rules={[
                    { required: true, message: "Please select a currency!" },
                  ]}
                  style={{ width: "100%" }}
                >
                  <Select
                    id="currency-select"
                    showSearch
                    style={{ width: "100%" }}
                    placeholder="Choose a currency"
                    optionFilterProp="children"
                    value={selectedCurrency}
                    defaultValue={organizationDetails.currency}
                    onChange={handleCurrencyChange}
                    filterOption={(input, option) =>
                      option?.children
                        ?.toString()
                        .toLowerCase()
                        .includes(input.toLowerCase()) ?? false
                    }
                  >
                    {Object.keys(groupedCurrencies).map((currencyCode) => (
                      <Select.OptGroup key={currencyCode} label={currencyCode}>
                        {groupedCurrencies[currencyCode].map(
                          (currency: any) => (
                            <Select.Option
                              key={`${currency.currency_code}-${currency.country}`}
                              value={`${currency.currency_code}-${currency.country}`}
                            >
                              {currency.country}&nbsp;(
                              <Typography.Paragraph
                                style={{ display: "inline" }}
                              >
                                {currency.currency_code}
                              </Typography.Paragraph>
                              )
                            </Select.Option>
                          ),
                        )}
                      </Select.OptGroup>
                    ))}
                  </Select>
                </Form.Item>
              </Flex>

              <Flex align="center" justify="space-between" gap={16}>
                <Form.Item
                  style={{ width: "100%" }}
                  label="Support Email"
                  name="supportEmail"
                  data-field="supportEmail"
                  rules={[
                    {
                      type: "email",
                      message: "Please enter a valid email address",
                    },
                    {
                      required: true,
                      message: "Please enter a support email",
                    },
                  ]}
                >
                  <Input
                    className="text-input"
                    placeholder="Enter support email"
                  />
                </Form.Item>
                <Form.Item
                  label="Industry"
                  name="industry"
                  data-field="industry"
                  style={{ width: "100%" }}
                >
                  <AutoComplete
                    className="auto_complete_component"
                    options={industriesOptions}
                    placeholder="Select Industry"
                  />
                </Form.Item>
              </Flex>

              <Form.Item
                label="Description"
                name="description"
                data-field="description"
                style={{ width: "100%" }}
              >
                <Input.TextArea
                  placeholder="Enter your organization's description"
                  autoSize={{ minRows: 5, maxRows: 6 }}
                />
              </Form.Item>

              <Flex align="center" justify="space-between" gap={16}>
                <Button
                  block
                  className="secondary-button"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button block type="primary" onClick={handleUpdate}>
                  Update
                </Button>
              </Flex>
            </Form>
          </div>
        </div>
      </div>
      <SubscriptionWarning
        open={open}
        handleClose={() => setOpen(false)}
        title="Upgrade"
        type="custom-domain"
      />
    </section>
  );
};

export default OrgDetails;
